import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { FiUsers, FiCheckSquare, FiEdit3, FiClipboard, FiTrendingUp } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import { getManagerDashboard } from "../../services/dashboardApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, StatTile, Loader, ErrorState, DataTable, StatusBadge, PersonCell, ProgressBar, EmptyState, ChartFrame } from "../../components/common";
import RatingPill from "../../components/performance/RatingPill";
import { axisProps, tooltipProps } from "../../utils/chartTheme";
import { firstName } from "../../utils/format";
import { formatRatingScore } from "../../utils/ratingScale"; // [NEW] PM-13: score on the chosen scale

const api = { getManagerDashboard };

export default function ManagerDashboard() {
  const { cycleId, cycle } = useCycle();
  const { profile } = useRole();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => api.getManagerDashboard(cycleId), [cycleId], { skip: !cycleId });

  if (loading || !data) return error ? <ErrorState message={error} onRetry={reload} /> : <Loader />;

  const chartData = data.team.map((t) => ({ name: firstName(t.employee.name), completion: t.goalCompletion ?? 0 }));

  return (
    <div className="page">
      <PageHeader title="Team overview" subtitle={`${profile.name}'s direct reports for ${cycle?.name}.`} />

      <div className="stat-grid">
        <StatTile icon={FiUsers} label="Direct reports" value={data.teamSize} onClick={() => navigate("/team")} />
        <StatTile icon={FiCheckSquare} tone="warning" label="Goals awaiting approval" value={data.pendingApprovals} hint="Review and approve" onClick={() => navigate("/team/goal-approvals")} />
        <StatTile icon={FiTrendingUp} tone="info" label="Progress updates to approve" value={data.pendingProgress} hint="Goals, KRAs and KPIs updated by your team" onClick={() => navigate("/team/progress-approvals")} />
        <StatTile icon={FiEdit3} tone="info" label="Self assessments pending" value={data.selfPending} hint="Not yet submitted by team" />
        <StatTile icon={FiClipboard} tone="danger" label="Your reviews pending" value={data.managerPending} hint={data.managerReviewOpen ? "Manager review is open" : "Window not open"} onClick={() => navigate("/team/assessments")} />
        <StatTile icon={FiTrendingUp} tone="success" label="Average goal completion" value={data.avgCompletion === null ? "—" : `${data.avgCompletion}%`} />
      </div>

      {data.teamSize === 0 ? (
        <Card><EmptyState title="No direct reports" text="Employees who report to you will appear here." /></Card>
      ) : (
        <>
          <Card title="Goal completion by team member" subtitle="Weighted achievement on approved goals">
            <ChartFrame
              label="Goal completion by team member, weighted achievement on approved goals"
              summary={chartData.map((d) => ({ label: d.name, value: `${d.completion}%` }))}
            >
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#EEF0F6" />
                  <XAxis dataKey="name" {...axisProps} />
                  <YAxis domain={[0, 100]} unit="%" {...axisProps} />
                  <Tooltip {...tooltipProps} formatter={(v) => [`${v}%`, "Completion"]} />
                  <Bar dataKey="completion" fill="#5B4FE0" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
          </Card>

          <Card title="Team status" padded={false}>
            <DataTable
              rowKey={(r) => r.employee.id}
              rows={data.team}
              onRowClick={(r) => navigate(`/team/${r.employee.id}`)}
              columns={[
                { key: "name", label: "Team member", render: (r) => <PersonCell name={r.employee.name} meta={r.employee.designationName} /> },
                { key: "sheet", label: "Goal sheet", render: (r) => <StatusBadge status={r.sheetStatus} /> },
                { key: "completion", label: "Completion", width: 160, render: (r) => <ProgressBar value={r.goalCompletion} showValue size="sm" /> },
                { key: "self", label: "Self assessment", render: (r) => <StatusBadge status={r.selfStatus} /> },
                { key: "mgr", label: "Your review", render: (r) => <StatusBadge status={r.managerStatus} /> },
                { key: "score", label: "Score", align: "right", render: (r) => formatRatingScore(r.performance.finalScore) },
                { key: "rating", label: "Rating", render: (r) => <RatingPill rating={r.resultStatus === "Awaiting Assessments" ? null : r.performance.rating} tone={r.performance.ratingTone} size="sm" /> },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}
